import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Sparkles, Send, Loader2, FileText, Eye, Download, ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY = {
  company_name: '', industry: '', annual_freight_volume: '',
  lanes_summary: '', pain_points: '',
  contact_name: '', contact_email: '',
  proposal_value: '', notes: '',
};

const EnterpriseProposalPage = () => {
  const [form, setForm] = useState(EMPTY);
  const [generating, setGenerating] = useState(false);
  const [current, setCurrent] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [sending, setSending] = useState(false);

  const loadList = async () => {
    try {
      const r = await axios.get(`${API}/enterprise/proposals?limit=20`);
      setProposals(r.data || []);
    } catch { /* ignore */ }
  };

  useEffect(() => { loadList(); }, []);

  const generate = async () => {
    if (!form.company_name.trim()) {
      toast.error('Company name is required');
      return;
    }
    setGenerating(true);
    setCurrent(null);
    try {
      const payload = { ...form };
      if (!payload.contact_email) delete payload.contact_email;
      const r = await axios.post(`${API}/enterprise/proposal/generate`, payload);
      setCurrent(r.data);
      toast.success('Proposal generated');
      loadList();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const openProposal = async (id) => {
    try {
      const r = await axios.get(`${API}/enterprise/proposal/${id}`);
      setCurrent(r.data);
    } catch {
      toast.error('Could not load proposal');
    }
  };

  const sendEmail = async () => {
    if (!current) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/enterprise/proposal/${current.id}/send`, {});
      if (r.data.sent) {
        toast.success(`Sent to ${r.data.to_email}`);
        setCurrent({ ...current, status: 'sent', sent_at: new Date().toISOString(), sent_to: r.data.to_email });
        loadList();
      } else if (r.data.suppressed) {
        toast.warning('Recipient is on unsubscribe list — not sent');
      } else {
        toast.error(r.data.error || 'Send failed');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const downloadHtml = () => {
    if (!current?.html) return;
    const blob = new Blob([current.html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `proposal-${current.company_name.replace(/[^a-z0-9]/gi, '_')}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadPdf = async () => {
    if (!current?.id) return;
    try {
      const r = await axios.get(`${API}/enterprise/proposal/${current.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Tempus_Proposal_${(current.company_name || 'proposal').replace(/[^a-z0-9]/gi, '_')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'PDF download failed');
    }
  };

  const openInNewTab = () => {
    if (!current?.html) return;
    const w = window.open('', '_blank');
    if (w) { w.document.write(current.html); w.document.close(); }
  };

  return (
    <Layout>
      <div className="p-4 lg:p-8 space-y-6" data-testid="enterprise-proposal-page">
        <div>
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-400" />
            <h1 className="text-3xl font-bold tracking-tight">Enterprise Managed Freight Proposal</h1>
          </div>
          <p className="text-muted-foreground text-sm mt-1 max-w-3xl">
            Generate a customised 1-page proposal positioning Tempus as a SaaS / Managed Freight partner — never as a broker.
            Powered by GPT-5.2. Output includes the legally-required "not a broker" disclaimer.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Form */}
          <Card className="lg:col-span-2" data-testid="proposal-form-card">
            <CardHeader className="pb-3"><CardTitle className="text-base">Prospect details</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <Row label="Company name *" testId="form-company">
                <Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} placeholder="e.g. Pinky Doors LLC" />
              </Row>
              <Row label="Industry">
                <Input value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} placeholder="e.g. Luxury door manufacturing" />
              </Row>
              <Row label="Annual freight volume">
                <Input value={form.annual_freight_volume} onChange={(e) => setForm({ ...form, annual_freight_volume: e.target.value })} placeholder="e.g. $28M / 4,200 loads/yr" />
              </Row>
              <Row label="Lanes summary">
                <Textarea rows={2} value={form.lanes_summary} onChange={(e) => setForm({ ...form, lanes_summary: e.target.value })} placeholder="e.g. Atlanta → DFW, Atlanta → MIA, Chicago → LA" />
              </Row>
              <Row label="Pain points">
                <Textarea rows={2} value={form.pain_points} onChange={(e) => setForm({ ...form, pain_points: e.target.value })} placeholder="e.g. Carrier sourcing time, missed pickup windows, broker fees eating margin" />
              </Row>
              <Row label="Contact name">
                <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} placeholder="e.g. Jane Cooper, VP Logistics" />
              </Row>
              <Row label="Contact email" testId="form-email">
                <Input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} placeholder="jane@pinkydoors.com" />
              </Row>
              <Row label="Proposed engagement value">
                <Input value={form.proposal_value} onChange={(e) => setForm({ ...form, proposal_value: e.target.value })} placeholder="e.g. $28,000/mo software + dispatch" />
              </Row>
              <Row label="Notes (optional)">
                <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Internal notes that should color the tone…" />
              </Row>
              <div className="flex gap-2">
                <Button
                  onClick={generate}
                  disabled={generating || !form.company_name.trim()}
                  className="flex-1"
                  data-testid="generate-proposal-btn"
                >
                  {generating
                    ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating</>
                    : <><Sparkles className="w-4 h-4 mr-2" /> Generate proposal</>}
                </Button>
                <Button variant="outline" onClick={() => { setForm(EMPTY); setCurrent(null); }} data-testid="reset-form-btn">
                  Reset
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Preview + actions */}
          <Card className="lg:col-span-3" data-testid="proposal-preview-card">
            <CardHeader className="pb-3 flex flex-row items-center justify-between gap-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Eye className="w-4 h-4" /> Preview
                {current?.status === 'sent' && (
                  <Badge className="bg-green-500/20 text-green-300 border-green-500/30" data-testid="proposal-status-sent">SENT</Badge>
                )}
              </CardTitle>
              {current && (
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={openInNewTab} data-testid="open-new-tab-btn">
                    <ExternalLink className="w-3.5 h-3.5 mr-1" /> Open
                  </Button>
                  <Button size="sm" variant="outline" onClick={downloadPdf} data-testid="download-pdf-btn">
                    <Download className="w-3.5 h-3.5 mr-1" /> PDF
                  </Button>
                  <Button size="sm" variant="outline" onClick={downloadHtml} data-testid="download-html-btn">
                    <Download className="w-3.5 h-3.5 mr-1" /> HTML
                  </Button>
                  <Button
                    size="sm"
                    onClick={sendEmail}
                    disabled={sending || !current.contact_email}
                    data-testid="send-proposal-btn"
                  >
                    {sending
                      ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Sending</>
                      : <><Send className="w-3.5 h-3.5 mr-1" /> Email to contact</>}
                  </Button>
                </div>
              )}
            </CardHeader>
            <CardContent>
              {!current ? (
                <div className="text-center py-16 text-sm text-muted-foreground" data-testid="proposal-preview-empty">
                  Fill in the form on the left and click <strong>Generate proposal</strong>. GPT-5.2 will compose a customised 1-page proposal here.
                </div>
              ) : (
                <iframe
                  title="proposal-preview"
                  srcDoc={current.html}
                  data-testid="proposal-preview-iframe"
                  className="w-full border border-border rounded-md bg-white"
                  style={{ height: '720px' }}
                />
              )}
            </CardContent>
          </Card>
        </div>

        {/* Saved proposals */}
        <Card data-testid="saved-proposals-card">
          <CardHeader className="pb-3"><CardTitle className="text-base">Saved proposals</CardTitle></CardHeader>
          <CardContent className="px-0">
            {proposals.length === 0 ? (
              <p className="text-sm text-muted-foreground p-4 text-center">No proposals yet.</p>
            ) : (
              <div className="divide-y divide-border">
                {proposals.map((p) => (
                  <div
                    key={p.id}
                    className="px-5 py-3 flex items-center gap-3 hover:bg-muted/30 cursor-pointer"
                    onClick={() => openProposal(p.id)}
                    data-testid={`proposal-row-${p.id}`}
                  >
                    <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{p.company_name}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {p.contact_name ? `${p.contact_name} · ` : ''}{p.contact_email || ''}
                        {p.proposal_value ? ` · ${p.proposal_value}` : ''}
                      </div>
                    </div>
                    <Badge
                      className={p.status === 'sent'
                        ? 'bg-green-500/20 text-green-300 border-green-500/30'
                        : 'bg-zinc-700/40 text-zinc-300 border-zinc-600'}
                    >
                      {p.status}
                    </Badge>
                    <span className="text-[11px] text-muted-foreground tabular-nums">
                      {new Date(p.created_at).toLocaleDateString()}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

const Row = ({ label, children, testId }) => (
  <div className="space-y-1" data-testid={testId}>
    <Label className="text-xs">{label}</Label>
    {children}
  </div>
);

export default EnterpriseProposalPage;
